import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  MessageSquare, Send, X, Maximize2, Minimize2, Plus, ChevronLeft,
  Paperclip, Hash, Users, Check, CheckCheck, FileIcon, Download,
  Search, User, XCircle, Loader2, Pin, Reply, AtSign, Smile, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const REACTIONS = [
  { key: 'check', display: '\u2705' },
  { key: 'thumbsup', display: '\ud83d\udc4d' },
  { key: 'eyes', display: '\ud83d\udc40' },
  { key: 'heart', display: '\u2764\ufe0f' },
  { key: 'fire', display: '\ud83d\udd25' },
];

const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-700', supervisor: 'bg-purple-100 text-purple-700',
  ventas: 'bg-blue-100 text-blue-700', disenador: 'bg-emerald-100 text-emerald-700',
  produccion: 'bg-amber-100 text-amber-700', worker: 'bg-gray-100 text-gray-700',
};

function MiniAvatar({ name, size = 32, isChannel }) {
  const initial = (name || '?')[0].toUpperCase();
  const colors = ['bg-red-500','bg-blue-500','bg-green-500','bg-purple-500','bg-amber-500','bg-cyan-500'];
  const color = colors[initial.charCodeAt(0) % colors.length];
  if (isChannel) return (
    <div className="flex items-center justify-center rounded-lg bg-[#F5F5F7] border border-[rgba(0,0,0,0.06)]" style={{ width: size, height: size }}>
      <Hash className="text-[#86868B]" style={{ width: size * 0.45, height: size * 0.45 }} />
    </div>
  );
  return (
    <div className={`${color} rounded-full flex items-center justify-center text-white font-semibold`} style={{ width: size, height: size, fontSize: size * 0.4 }}>
      {initial}
    </div>
  );
}

function ImageLightbox({ src, alt, onClose }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full text-white"><X className="w-6 h-6" /></button>
      <img src={src} alt={alt} className="max-w-full max-h-[90vh] rounded-lg object-contain" onClick={e => e.stopPropagation()} />
    </div>
  );
}

// ─── New Chat Modal ────────────────────────────────────────
function NewChatModal({ onClose, onCreated, userRole }) {
  const [mode, setMode] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [channelName, setChannelName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [creating, setCreating] = useState(false);
  const isAdmin = userRole === 'admin' || userRole === 'supervisor';

  useEffect(() => { if (mode) loadContacts(); }, [mode]);
  const loadContacts = async () => {
    setLoading(true);
    try { const { data } = await api.get('/chat/contacts'); setContacts(data.contacts || []); } catch {}
    setLoading(false);
  };
  const filtered = contacts.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase()));

  const startDM = async (contact) => {
    setCreating(true);
    try { const { data } = await api.post('/chat/conversations', { type: 'direct', participant_ids: [contact.user_id] }); onCreated(data); } catch { toast.error('Error al crear conversacion'); }
    setCreating(false);
  };
  const createChannel = async () => {
    if (!channelName.trim() || selectedMembers.length === 0) return;
    setCreating(true);
    try { const { data } = await api.post('/chat/conversations', { type: 'group', name: channelName.trim(), participant_ids: selectedMembers.map(m => m.user_id) }); onCreated(data); } catch { toast.error('Error al crear canal'); }
    setCreating(false);
  };
  const toggleMember = (contact) => {
    setSelectedMembers(prev => prev.some(m => m.user_id === contact.user_id) ? prev.filter(m => m.user_id !== contact.user_id) : [...prev, contact]);
  };

  if (!mode) return (
    <div className="absolute inset-0 z-10 bg-white flex flex-col rounded-2xl" data-testid="new-chat-modal">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-semibold text-[#1D1D1F]">Nueva conversacion</h3>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4 text-[#86868B]" /></button>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6">
        <button onClick={() => setMode('dm')} className="w-full flex items-center gap-3 p-4 rounded-xl border border-[rgba(0,0,0,0.08)] hover:bg-[#F5F5F7] transition" data-testid="new-dm-btn">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><User className="w-5 h-5 text-blue-500" /></div>
          <div className="text-left"><p className="font-medium text-[#1D1D1F]">Mensaje directo</p><p className="text-xs text-[#86868B]">Chat 1 a 1 con un miembro</p></div>
        </button>
        <button onClick={() => setMode('channel')} className="w-full flex items-center gap-3 p-4 rounded-xl border border-[rgba(0,0,0,0.08)] hover:bg-[#F5F5F7] transition" data-testid="new-channel-btn">
          <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center"><Users className="w-5 h-5 text-[#E86A1F]" /></div>
          <div className="text-left"><p className="font-medium text-[#1D1D1F]">Nuevo grupo</p><p className="text-xs text-[#86868B]">Habla con quienes elijas</p></div>
        </button>
      </div>
    </div>
  );

  if (mode === 'dm') return (
    <div className="absolute inset-0 z-10 bg-white flex flex-col rounded-2xl" data-testid="dm-contact-picker">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <button onClick={() => setMode(null)} className="p-1 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-4 h-4 text-[#86868B]" /></button>
        <h3 className="font-semibold text-[#1D1D1F] flex-1">Mensaje directo</h3>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4 text-[#86868B]" /></button>
      </div>
      <div className="px-3 py-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#AEAEB2]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar miembro..." autoFocus className="w-full pl-9 pr-3 py-2 bg-[#F5F5F7] rounded-lg text-sm border-0 focus:outline-none focus:ring-1 focus:ring-[#E86A1F]/30" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#86868B]" /></div>
        : filtered.filter(c => c.type === 'team').map(contact => (
          <button key={contact.user_id} onClick={() => startDM(contact)} disabled={creating} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F5F5F7] transition disabled:opacity-50" data-testid={`contact-${contact.user_id}`}>
            <MiniAvatar name={contact.name} />
            <div className="flex-1 text-left min-w-0"><p className="text-sm font-medium text-[#1D1D1F] truncate">{contact.name}</p><p className="text-xs text-[#86868B]">{contact.email}</p></div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${ROLE_COLORS[contact.system_role] || 'bg-gray-100 text-gray-600'}`}>{contact.system_role}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="absolute inset-0 z-10 bg-white flex flex-col rounded-2xl" data-testid="channel-creator">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <button onClick={() => setMode(null)} className="p-1 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-4 h-4 text-[#86868B]" /></button>
        <h3 className="font-semibold text-[#1D1D1F] flex-1">Nuevo grupo</h3>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4 text-[#86868B]" /></button>
      </div>
      <div className="p-4 space-y-3">
        <div><label className="text-xs font-medium text-[#86868B] mb-1 block">Nombre del grupo</label>
          <div className="flex items-center gap-2"><Hash className="w-4 h-4 text-[#AEAEB2]" /><input value={channelName} onChange={e => setChannelName(e.target.value)} placeholder="ej: urgentes, ideas..." autoFocus className="flex-1 py-2 bg-transparent text-sm border-b border-gray-200 focus:border-[#E86A1F] focus:outline-none" /></div>
        </div>
        <div><label className="text-xs font-medium text-[#86868B] mb-1 block">Miembros ({selectedMembers.length})</label>
          {selectedMembers.length > 0 && <div className="flex flex-wrap gap-1 mb-2">{selectedMembers.map(m => <span key={m.user_id} className="flex items-center gap-1 px-2 py-1 bg-orange-50 text-[#E86A1F] rounded-full text-xs">{m.name}<button onClick={() => toggleMember(m)}><XCircle className="w-3 h-3" /></button></span>)}</div>}
          <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#AEAEB2]" /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..." className="w-full pl-9 pr-3 py-2 bg-[#F5F5F7] rounded-lg text-sm border-0 focus:outline-none focus:ring-1 focus:ring-[#E86A1F]/30" /></div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-2">
        {loading ? <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#86868B]" /></div>
        : filtered.filter(c => c.type === 'team').map(contact => {
          const sel = selectedMembers.some(m => m.user_id === contact.user_id);
          return (<button key={contact.user_id} onClick={() => toggleMember(contact)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition ${sel ? 'bg-orange-50' : 'hover:bg-[#F5F5F7]'}`}><MiniAvatar name={contact.name} size={28} /><span className="text-sm text-[#1D1D1F] flex-1 text-left truncate">{contact.name}</span>{sel && <Check className="w-4 h-4 text-[#E86A1F]" />}</button>);
        })}
      </div>
      <div className="p-3 border-t border-gray-100">
        <button onClick={createChannel} disabled={creating || !channelName.trim() || selectedMembers.length === 0} className="w-full py-2.5 bg-gradient-to-r from-[#e03c3c] to-[#E86A1F] text-white rounded-xl text-sm font-semibold disabled:opacity-40 transition" data-testid="create-channel-btn">{creating ? 'Creando...' : 'Crear grupo'}</button>
      </div>
    </div>
  );
}

// ─── Message Bubble with Reactions + Reply ─────────────────
function MessageBubble({ msg, isOwn, showSender, onImageClick, onReaction, onReply, onPin, userId, isAdmin }) {
  const [showReactions, setShowReactions] = useState(false);
  const time = new Date(msg.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  const isFile = msg.type === 'file' || msg.type === 'image' || msg.type === 'attachment';
  const isImage = msg.type === 'image' || (msg.file_url && /\.(jpg|jpeg|png|gif|webp)$/i.test(msg.file_url));
  const isDeleted = msg.type === 'deleted';
  const reactions = msg.reactions || [];

  // Group reactions by emoji
  const grouped = {};
  reactions.forEach(r => {
    if (!grouped[r.emoji]) grouped[r.emoji] = { count: 0, users: [], mine: false };
    grouped[r.emoji].count++;
    grouped[r.emoji].users.push(r.user_name);
    if (r.user_id === userId) grouped[r.emoji].mine = true;
  });

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group relative`}
      onMouseLeave={() => setShowReactions(false)}>
      <div className="max-w-[80%] relative">
        {/* Reply preview */}
        {msg.reply_to && (
          <div className={`mb-0.5 px-3 py-1 rounded-t-lg text-[10px] border-l-2 ${isOwn ? 'bg-orange-100/60 border-[#E86A1F] text-[#6E6E73]' : 'bg-gray-100 border-gray-400 text-[#86868B]'}`}>
            <span className="font-semibold">{msg.reply_to.sender_name}</span>: {msg.reply_to.content_preview}
          </div>
        )}
        <div className={`px-3 py-1.5 rounded-xl text-sm ${
          isDeleted ? 'bg-gray-100 text-[#AEAEB2] italic' :
          isOwn ? 'bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] text-white rounded-br-sm' : 'bg-[#F5F5F7] text-[#1D1D1F] rounded-bl-sm'
        } ${msg.reply_to ? 'rounded-t-none' : ''}`}>
          {showSender && !isOwn && !isDeleted && (
            <p className="text-[10px] font-semibold mb-0.5 text-[#E86A1F]">{msg.sender_name}</p>
          )}
          {isImage && msg.file_url && (
            <button onClick={() => onImageClick(msg.file_url, msg.file_name)} className="block mb-1 rounded-lg overflow-hidden">
              <img 
                src={msg.file_url} 
                alt={msg.file_name || 'Imagen'} 
                className="max-w-full rounded-lg max-h-48 object-cover hover:opacity-90 transition" 
                data-testid="chat-image-attachment"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling && (e.target.nextElementSibling.style.display = 'flex');
                }}
              />
              <div className="hidden items-center gap-2 p-3 bg-gray-100 rounded-lg text-sm text-gray-500">
                <ImageIcon className="w-4 h-4" />
                <span>Imagen no disponible</span>
              </div>
            </button>
          )}
          {isFile && !isImage && msg.file_url && (
            <a href={msg.file_url} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 p-2 rounded-lg mb-1 ${isOwn ? 'bg-white/20 hover:bg-white/30' : 'bg-white hover:bg-gray-50 border border-[rgba(0,0,0,0.06)]'} transition-colors`} data-testid="chat-file-attachment">
              <FileIcon className="w-5 h-5 flex-shrink-0" /><div className="flex-1 min-w-0"><span className="text-xs font-medium truncate block">{msg.file_name || 'Archivo'}</span></div><Download className="w-4 h-4 flex-shrink-0 opacity-60" />
            </a>
          )}
          {msg.is_pinned && <div className="flex items-center gap-1 text-[9px] opacity-60 mb-0.5"><Pin className="w-2.5 h-2.5" /> Fijado</div>}
          {/* Render content with @mention highlights */}
          {msg.content && <p className="whitespace-pre-wrap leading-relaxed">{renderMentions(msg.content, isOwn)}</p>}
          <div className="flex items-center justify-end gap-1 mt-0.5">
            {isOwn && !isDeleted && <ReadIndicator msg={msg} />}
            <span className={`text-[9px] ${isOwn ? 'text-white/50' : 'text-[#AEAEB2]'}`}>{time}</span>
          </div>
        </div>

        {/* Reactions display */}
        {Object.keys(grouped).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-0.5 px-1">
            {Object.entries(grouped).map(([emoji, data]) => {
              const display = REACTIONS.find(r => r.key === emoji)?.display || emoji;
              return (
                <button key={emoji} onClick={() => onReaction(msg.message_id, emoji)}
                  className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] border transition ${data.mine ? 'bg-orange-50 border-[#E86A1F]/30 text-[#E86A1F]' : 'bg-white border-gray-200 text-[#6E6E73] hover:bg-gray-50'}`}
                  title={data.users.join(', ')} data-testid={`reaction-${emoji}-${msg.message_id}`}>
                  {display} <span>{data.count}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Hover actions */}
        {!isDeleted && (
          <div className={`absolute top-0 ${isOwn ? 'left-0 -translate-x-full' : 'right-0 translate-x-full'} hidden group-hover:flex items-center gap-0.5 px-1`}>
            <button onClick={() => setShowReactions(!showReactions)} className="p-1 rounded bg-white shadow-sm border border-gray-100 hover:bg-gray-50 text-[#86868B]" title="Reaccionar">
              <Smile className="w-3 h-3" />
            </button>
            <button onClick={() => onReply(msg)} className="p-1 rounded bg-white shadow-sm border border-gray-100 hover:bg-gray-50 text-[#86868B]" title="Responder">
              <Reply className="w-3 h-3" />
            </button>
            {isAdmin && (
              <button onClick={() => onPin(msg)} className="p-1 rounded bg-white shadow-sm border border-gray-100 hover:bg-gray-50 text-[#86868B]" title={msg.is_pinned ? 'Desfijar' : 'Fijar'}>
                <Pin className="w-3 h-3" />
              </button>
            )}
          </div>
        )}

        {/* Reaction picker popup */}
        {showReactions && (
          <div className={`absolute ${isOwn ? 'right-0' : 'left-0'} -top-8 flex items-center gap-0.5 bg-white rounded-full shadow-lg border border-gray-100 px-1.5 py-1 z-20`}>
            {REACTIONS.map(r => (
              <button key={r.key} onClick={() => { onReaction(msg.message_id, r.key); setShowReactions(false); }}
                className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center text-sm transition" data-testid={`react-${r.key}`}>
                {r.display}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function renderMentions(text, isOwn) {
  const parts = text.split(/(@\w+(?:\s\w+)?)/g);
  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      return <span key={i} className={`font-semibold ${isOwn ? 'text-white underline' : 'text-[#E86A1F]'}`}>{part}</span>;
    }
    return part;
  });
}

function ReadIndicator({ msg }) {
  const readCount = (msg.read_by || []).length;
  if (readCount >= 2) return <CheckCheck className="w-3 h-3 text-blue-300" />;
  return <Check className="w-3 h-3 text-white/50" />;
}

// ─── Search Panel ──────────────────────────────────────────
function SearchPanel({ onClose, onNavigate }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const doSearch = async () => {
    if (query.length < 2) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/chat/search?q=${encodeURIComponent(query)}`);
      setResults(data.results || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    const t = setTimeout(doSearch, 400);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <div className="absolute inset-0 z-10 bg-white flex flex-col rounded-2xl" data-testid="search-panel">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <Search className="w-4 h-4 text-[#AEAEB2]" />
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Buscar mensajes..." autoFocus
          className="flex-1 text-sm bg-transparent focus:outline-none text-[#1D1D1F] placeholder-[#AEAEB2]" />
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4 text-[#86868B]" /></button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading && <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#86868B]" /></div>}
        {!loading && results.length === 0 && query.length >= 2 && (
          <p className="text-center text-sm text-[#AEAEB2] py-8">Sin resultados para "{query}"</p>
        )}
        {results.map(r => (
          <button key={r.message_id} onClick={() => onNavigate(r.conversation_id)} className="w-full text-left px-4 py-3 hover:bg-[#F5F5F7] transition border-b border-gray-50">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium text-[#E86A1F]">#{r.conversation_name}</span>
              <span className="text-[10px] text-[#AEAEB2]">{new Date(r.created_at).toLocaleDateString('es-MX')}</span>
            </div>
            <p className="text-xs text-[#86868B]">{r.sender_name}:</p>
            <p className="text-sm text-[#1D1D1F] line-clamp-2">{r.content}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Pinned Messages Panel ─────────────────────────────────
function PinnedPanel({ conversationId, onClose }) {
  const [pinned, setPinned] = useState([]);
  useEffect(() => {
    api.get(`/chat/conversations/${conversationId}/pinned`).then(({ data }) => setPinned(data.pinned || [])).catch(() => {});
  }, [conversationId]);
  return (
    <div className="absolute inset-0 z-10 bg-white flex flex-col rounded-2xl" data-testid="pinned-panel">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2"><Pin className="w-4 h-4 text-[#E86A1F]" /><h3 className="font-semibold text-[#1D1D1F]">Mensajes fijados</h3></div>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4 text-[#86868B]" /></button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {pinned.length === 0 ? <p className="text-center text-sm text-[#AEAEB2] py-8">Sin mensajes fijados</p>
        : pinned.map(p => (
          <div key={p.message_id} className="px-4 py-3 border-b border-gray-50">
            <p className="text-xs font-medium text-[#E86A1F] mb-0.5">{p.sender_name}</p>
            <p className="text-sm text-[#1D1D1F]">{p.content}</p>
            <p className="text-[10px] text-[#AEAEB2] mt-1">Fijado por {p.pinned_by} — {new Date(p.pinned_at).toLocaleDateString('es-MX')}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Mention Autocomplete ──────────────────────────────────
function MentionAutocomplete({ participants, search, onSelect }) {
  const filtered = participants.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase())
  );
  if (filtered.length === 0 && search !== 'todos') return null;
  return (
    <div className="absolute bottom-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mb-1 max-h-40 overflow-y-auto z-20" data-testid="mention-autocomplete">
      <button onClick={() => onSelect({ user_id: 'all', name: 'todos' })} className="w-full text-left px-3 py-2 hover:bg-[#F5F5F7] text-sm flex items-center gap-2">
        <Users className="w-4 h-4 text-[#E86A1F]" /> <span className="font-medium">@todos</span> <span className="text-xs text-[#86868B]">Notificar a todo el canal</span>
      </button>
      {filtered.map(p => (
        <button key={p.user_id} onClick={() => onSelect(p)} className="w-full text-left px-3 py-2 hover:bg-[#F5F5F7] text-sm flex items-center gap-2">
          <MiniAvatar name={p.name} size={20} /> <span>{p.name}</span>
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN CHAT WIDGET
// ═══════════════════════════════════════════════════════════
export default function ChatWidget() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showPinned, setShowPinned] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [mentionSearch, setMentionSearch] = useState(null);
  const messagesEndRef = useRef(null);
  const shouldScrollRef = useRef(true);
  const pollRef = useRef(null);
  const typingRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const notifAudioRef = useRef(null);
  const prevMsgCountRef = useRef({});

  const user = JSON.parse(localStorage.getItem('portal_user') || localStorage.getItem('worker_user') || '{}');
  const userId = user.user_id || user.worker_id;
  const userRole = user.system_role || user.role || 'worker';
  const isAdmin = userRole === 'admin' || userRole === 'supervisor';
  const token = localStorage.getItem('portal_token') || localStorage.getItem('worker_token');

  // ─── Listen for external "open-chat-channel" events ──────
  useEffect(() => {
    const handler = async (e) => {
      const { conversationId } = e.detail || {};
      if (!conversationId) return;
      setIsOpen(true);
      setShowNewChat(false);
      setShowSearch(false);
      setShowPinned(false);
      try {
        const { data } = await api.get(`/chat/conversations/${conversationId}`);
        if (data && !data.error) {
          setActiveConv(data);
          fetchConversations();
        } else {
          toast.error('No se pudo abrir la conversacion');
        }
      } catch {
        toast.error('Error al abrir canal');
      }
    };
    window.addEventListener('open-chat-channel', handler);
    return () => window.removeEventListener('open-chat-channel', handler);
  }, []);

  // ─── Data fetching ───────────────────────────────────────
  const fetchUnread = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await api.get('/chat/unread-count');
      const newCount = data.unread || 0;
      if (newCount > unreadCount && unreadCount >= 0) {
        // Play notification sound for new messages
        try {
          if (!notifAudioRef.current) {
            notifAudioRef.current = new Audio('data:audio/wav;base64,UklGRlQFAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YTAFAABkAGQAZABkAGQAZABkAGQAZABkAKYA5gAmAWYBpgHmASYCZgKmAuYCJgNmA6YD5gMmBGYEZgRmBGYEZgRmBGYEZgRmBCYEJgQGBAYEBgP2A+YD5gPmA8YD1gO2A5YDhgN2A2YDVgNGA0YDNgMmAyYDJgMGAwYDBgMGAuYC5gLmAuYC1gLWAtYCxgLGAsYCpgKmAqYChgKGAnYCZgJWAlYCRgJGAjYCNgImAiYCFgIWAgYCBgH2AfYB5gHmAdYB1gHGAcYBtgG2AaYBpgGWAZYBhgGGAXYBdgFmAWYBVgFWAUYBRgE2ATYBJgEmARYBFgEGAQYA9gD2AOYA5gDWANYAxgDGALYAtgCmAKYAlgCWAIYAhgB2AHYAZgBmAFYAVgBGAEYANgA2ACYAJgAWABYABgAGD/X/9f/l/+X/1f/V/8X/xf+1/7X/pf+l/5X/lf+F/4X/df91/2X/Zf9V/1X/Rf9F/zX/Nf8l/yX/Ff8V/wX/Bf71/vX+5f7l/tX+1f7F/sX+tf61/qX+pf6V/pX+hf6F/nX+df5l/mX+Vf5V/kX+Rf41/jX+Jf4l/hX+Ff4F/gX99f31/eX95f3V/dX9xf3F/bX9tf2l/aX9lf2V/YX9hf2F/YX9hf2F/YX9hf2V/ZX9pf2l/bX9tf3F/cX91f3V/eX95f31/fX+Bf4F/hX+Ff4l/iX+Nf41/kX+Rf5V/lX+Zf5l/nX+df6F/oX+lf6V/qX+pf61/rX+xf7F/tX+1f7l/uX+9f71/wX/Bf8V/xX/Jf8l/zX/Nf9F/0X/Vf9V/2X/Zf91/3X/hf+F/5X/lf+l/6X/tf+1/8X/xf/V/9X/5f/l//X/9fAGAAYAFgAWACYAJgA2ADYARgBGAFYAVgBmAGYAdgB2AIYAhgCWAJYApgCmALYAtgDGAMYA1gDWAOYA5gD2APYBBgEGARYBFgEmASYBNgE2AUYBRgFWAVYBZgFmAXYBdgGGAYYBlgGWAaYBpgG2AbYBxgHGAdYB1gHmAeYB9gH2AgYCBgIWAhYCJgImAjYCNgJGAkYCVgJWAmYCZgJ2AnYChgKGApYClgKmAqYCtgK2AsYCxgLWAtYC5gLmAvYC9gMGAwYA==');
          }
          notifAudioRef.current.volume = 0.3;
          notifAudioRef.current.play().catch(() => {});
        } catch {}
      }
      setUnreadCount(newCount);
    } catch {}
  }, [token, unreadCount]);

  const fetchConversations = useCallback(async () => {
    try { const { data } = await api.get('/chat/conversations'); setConversations(data.conversations || []); } catch {}
  }, []);

  const fetchMessages = useCallback(async (convId) => {
    try {
      const { data } = await api.get(`/chat/conversations/${convId}/messages?limit=50`);
      setMessages(data.messages || []);
      api.put(`/chat/conversations/${convId}/read`).catch(() => {});
    } catch {}
  }, []);

  const fetchTyping = useCallback(async (convId) => {
    try { const { data } = await api.get(`/chat/conversations/${convId}/typing`); setTypingUsers(data.typing || []); } catch {}
  }, []);

  // ─── Ensure default channels on first open ───────────────
  const ensuredRef = useRef(false);
  const ensureChannels = useCallback(async () => {
    if (ensuredRef.current) return;
    ensuredRef.current = true;
    try { await api.post('/chat/channels/ensure-defaults'); } catch {}
  }, []);

  // ─── Effects ─────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    fetchUnread();
    const interval = setInterval(fetchUnread, 5000);
    return () => clearInterval(interval);
  }, [token, fetchUnread]);

  useEffect(() => {
    if (isOpen && token) { ensureChannels().then(fetchConversations); }
  }, [isOpen, token, fetchConversations, ensureChannels]);

  useEffect(() => {
    if (!activeConv) { setTypingUsers([]); return; }
    fetchMessages(activeConv.conversation_id);
    pollRef.current = setInterval(() => {
      fetchMessages(activeConv.conversation_id);
      fetchTyping(activeConv.conversation_id);
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [activeConv, fetchMessages, fetchTyping]);

  useEffect(() => {
    if (shouldScrollRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  if (!token || !userId) return null;
  if (location.pathname.includes('/chat')) return null;

  // ─── Handlers ────────────────────────────────────────────
  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !activeConv || sending) return;
    setSending(true);
    try {
      const mentions = [];
      let mentionAll = false;
      const mentionMatches = newMessage.match(/@(\w+(?:\s\w+)?)/g);
      if (mentionMatches) {
        for (const m of mentionMatches) {
          const name = m.slice(1);
          if (name === 'todos') { mentionAll = true; continue; }
          const p = (activeConv.participants || []).find(pp => pp.name?.toLowerCase().includes(name.toLowerCase()));
          if (p) mentions.push(p.user_id);
        }
      }

      const body = {
        content: newMessage, type: 'text', mentions,
        mention_all: mentionAll,
        reply_to: replyTo ? { message_id: replyTo.message_id, sender_name: replyTo.sender_name, content_preview: (replyTo.content || '').slice(0, 60) } : undefined
      };

      if (mentions.length > 0 || mentionAll) {
        const { data } = await api.post(`/chat/conversations/${activeConv.conversation_id}/messages-with-mentions`, body);
        setMessages(prev => [...prev, data]);
      } else {
        const { data } = await api.post(`/chat/conversations/${activeConv.conversation_id}/messages`, body);
        setMessages(prev => [...prev, data]);
      }
      setNewMessage('');
      setReplyTo(null);
      setMentionSearch(null);
      shouldScrollRef.current = true;
      fetchConversations();
    } catch { toast.error('Error al enviar'); }
    setSending(false);
  };

  const handleTyping = () => {
    if (!activeConv) return;
    if (typingRef.current) clearTimeout(typingRef.current);
    api.post(`/chat/conversations/${activeConv.conversation_id}/typing`).catch(() => {});
    typingRef.current = setTimeout(() => {}, 3000);
  };

  const handleInputChange = (val) => {
    setNewMessage(val);
    handleTyping();
    // Detect @mention
    const lastAt = val.lastIndexOf('@');
    if (lastAt !== -1 && lastAt === val.length - 1) {
      setMentionSearch('');
    } else if (lastAt !== -1) {
      const afterAt = val.slice(lastAt + 1);
      if (!afterAt.includes(' ') || afterAt.split(' ').length <= 2) {
        setMentionSearch(afterAt);
      } else {
        setMentionSearch(null);
      }
    } else {
      setMentionSearch(null);
    }
  };

  const handleMentionSelect = (participant) => {
    const lastAt = newMessage.lastIndexOf('@');
    const before = newMessage.slice(0, lastAt);
    setNewMessage(`${before}@${participant.name} `);
    setMentionSearch(null);
    inputRef.current?.focus();
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !activeConv) return;
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} es demasiado grande (max 10MB)`); continue; }
      setUploading(true);
      try {
        const formData = new FormData(); formData.append('file', file);
        const { data: up } = await api.post('/chat/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        const isImg = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name);
        const { data: msgData } = await api.post(`/chat/conversations/${activeConv.conversation_id}/messages`, {
          content: '', type: isImg ? 'image' : 'file', file_url: up.file_url, file_name: up.file_name, file_size: up.file_size
        });
        setMessages(prev => [...prev, msgData]);
        fetchConversations();
      } catch { toast.error(`Error subiendo ${file.name}`); }
      setUploading(false);
    }
    e.target.value = '';
  };

  const handleReaction = async (messageId, emoji) => {
    try {
      const { data } = await api.post(`/chat/messages/${messageId}/reactions`, { emoji });
      setMessages(prev => prev.map(m => m.message_id === messageId ? { ...m, reactions: data.reactions } : m));
    } catch {}
  };

  const handlePin = async (msg) => {
    if (!activeConv) return;
    try {
      if (msg.is_pinned) {
        await api.delete(`/chat/conversations/${activeConv.conversation_id}/pin/${msg.message_id}`);
        setMessages(prev => prev.map(m => m.message_id === msg.message_id ? { ...m, is_pinned: false } : m));
        toast.success('Mensaje desfijado');
      } else {
        await api.post(`/chat/conversations/${activeConv.conversation_id}/pin/${msg.message_id}`);
        setMessages(prev => prev.map(m => m.message_id === msg.message_id ? { ...m, is_pinned: true } : m));
        toast.success('Mensaje fijado');
      }
    } catch { toast.error('Error'); }
  };

  const handleNewChatCreated = (conv) => { setShowNewChat(false); shouldScrollRef.current = true; setActiveConv(conv); fetchConversations(); };

  const handleSearchNavigate = async (convId) => {
    setShowSearch(false);
    const conv = conversations.find(c => c.conversation_id === convId);
    if (conv) setActiveConv(conv);
    else { try { const { data } = await api.get(`/chat/conversations/${convId}`); setActiveConv(data); } catch {} }
  };

  const getConvName = (conv) => {
    if (conv.type === 'direct') return conv.display_name || conv.participants?.find(p => p.user_id !== userId)?.name || 'Chat';
    return conv.name || 'Grupo';
  };
  const isChannel = (conv) => conv.type !== 'direct';

  const channels = conversations.filter(c => isChannel(c));
  const directMessages = conversations.filter(c => c.type === 'direct');

  const panelClass = isExpanded
    ? 'fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[440px] md:w-[520px] rounded-none sm:rounded-l-2xl shadow-2xl'
    : 'fixed right-6 z-50 w-[360px] h-[520px] rounded-2xl shadow-2xl ios-chat-widget-pos';

  return (
    <>
      {/* ═══ FLOATING BUBBLE ═══ */}
      {!isOpen && (
        <button data-testid="chat-widget-bubble" onClick={() => setIsOpen(true)}
          className="fixed right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center ios-chat-widget-pos">
          <MessageSquare className="w-6 h-6" />
          {unreadCount > 0 && (
            <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-600 flex items-center justify-center">
              <span className="text-[10px] text-white font-bold">{unreadCount > 99 ? '99' : unreadCount}</span>
            </div>
          )}
        </button>
      )}

      {/* ═══ CHAT PANEL ═══ */}
      {isOpen && (
        <div data-testid="chat-widget-panel" className={`${panelClass} bg-white border border-gray-200 flex flex-col overflow-hidden transition-all duration-200`}>

          {/* ─── Header ─── */}
          <div className="px-4 py-3 bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] text-white flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              {activeConv && (
                <button onClick={() => { setActiveConv(null); setMessages([]); setTypingUsers([]); setReplyTo(null); setShowPinned(false); }}
                  className="p-1 rounded-lg hover:bg-white/20" data-testid="widget-back-btn">
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
              {activeConv ? (
                <div className="flex items-center gap-2 min-w-0">
                  <MiniAvatar name={getConvName(activeConv)} size={24} isChannel={isChannel(activeConv)} />
                  <div className="min-w-0">
                    <span className="font-semibold text-sm truncate block">{isChannel(activeConv) ? '#' : ''}{getConvName(activeConv)}</span>
                    {activeConv.participants && <p className="text-[10px] text-white/60">{activeConv.participants.length} miembros</p>}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2"><MessageSquare className="w-5 h-5" /><span className="font-semibold text-sm">Chat</span></div>
              )}
            </div>
            <div className="flex items-center gap-0.5">
              {activeConv && isChannel(activeConv) && (
                <button onClick={() => setShowPinned(true)} className="p-1.5 rounded-lg hover:bg-white/20" title="Fijados" data-testid="widget-pinned-btn">
                  <Pin className="w-4 h-4" />
                </button>
              )}
              {!activeConv && (
                <>
                  <button onClick={() => setShowSearch(true)} className="p-1.5 rounded-lg hover:bg-white/20" title="Buscar" data-testid="widget-search-btn">
                    <Search className="w-4 h-4" />
                  </button>
                  <button onClick={() => setShowNewChat(true)} className="p-1.5 rounded-lg hover:bg-white/20" title="Nueva conversacion" data-testid="widget-new-chat-btn">
                    <Plus className="w-4 h-4" />
                  </button>
                </>
              )}
              <button onClick={() => setIsExpanded(!isExpanded)} className="p-1.5 rounded-lg hover:bg-white/20" title={isExpanded ? 'Reducir' : 'Expandir'} data-testid="widget-expand-btn">
                {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button onClick={() => { setIsOpen(false); setActiveConv(null); setIsExpanded(false); setShowNewChat(false); setShowSearch(false); setShowPinned(false); setReplyTo(null); }}
                className="p-1.5 rounded-lg hover:bg-white/20" data-testid="widget-close-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* ─── Overlay panels ─── */}
          {showNewChat && <NewChatModal onClose={() => setShowNewChat(false)} onCreated={handleNewChatCreated} userRole={userRole} />}
          {showSearch && <SearchPanel onClose={() => setShowSearch(false)} onNavigate={handleSearchNavigate} />}
          {showPinned && activeConv && <PinnedPanel conversationId={activeConv.conversation_id} onClose={() => setShowPinned(false)} />}

          {/* ─── Conversation List ─── */}
          {!activeConv && !showNewChat && !showSearch && (
            <div className="flex-1 overflow-y-auto" data-testid="widget-conversation-list">
              {conversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6">
                  <MessageSquare className="w-8 h-8 mb-2 opacity-40" />
                  <p className="text-sm font-medium text-[#6E6E73]">Sin conversaciones</p>
                  <p className="text-xs text-[#AEAEB2] mt-1 text-center">Presiona + para iniciar un chat</p>
                  <button onClick={() => setShowNewChat(true)} className="mt-3 px-4 py-2 bg-gradient-to-r from-[#e03c3c] to-[#E86A1F] text-white rounded-xl text-sm font-medium" data-testid="empty-new-chat-btn">
                    <Plus className="w-4 h-4 inline mr-1" /> Nuevo chat
                  </button>
                </div>
              ) : (
                <>
                  {channels.length > 0 && (
                    <>
                      <div className="px-4 pt-3 pb-1"><p className="text-[10px] font-semibold text-[#AEAEB2] uppercase tracking-wider">Grupos</p></div>
                      {channels.map(conv => <ConvListItem key={conv.conversation_id} conv={conv} userId={userId} onClick={() => setActiveConv(conv)} getConvName={getConvName} isChannel />)}
                    </>
                  )}
                  {directMessages.length > 0 && (
                    <>
                      <div className="px-4 pt-3 pb-1"><p className="text-[10px] font-semibold text-[#AEAEB2] uppercase tracking-wider">Mensajes directos</p></div>
                      {directMessages.map(conv => <ConvListItem key={conv.conversation_id} conv={conv} userId={userId} onClick={() => setActiveConv(conv)} getConvName={getConvName} />)}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* ─── Messages View ─── */}
          {activeConv && !showNewChat && !showSearch && !showPinned && (
            <>
              <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5" data-testid="widget-messages"
                onScroll={(e) => { const el = e.target; shouldScrollRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60; }}>
                {messages.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full text-[#AEAEB2]">
                    <MessageSquare className="w-6 h-6 mb-1 opacity-40" /><p className="text-xs">Sin mensajes aun. Escribe el primero.</p>
                  </div>
                )}
                {messages.map(msg => (
                  <MessageBubble key={msg.message_id} msg={msg} isOwn={msg.sender_id === userId}
                    showSender={isChannel(activeConv)} userId={userId} isAdmin={isAdmin}
                    onImageClick={(src, alt) => setLightboxImg({ src, alt })}
                    onReaction={handleReaction} onReply={(m) => { setReplyTo(m); inputRef.current?.focus(); }}
                    onPin={handlePin} />
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Typing indicator */}
              {typingUsers.length > 0 && (
                <div className="px-4 py-1 text-xs text-[#86868B] italic" data-testid="typing-indicator">
                  {typingUsers.map(t => t.name).join(', ')} {typingUsers.length === 1 ? 'esta' : 'estan'} escribiendo...
                </div>
              )}

              {/* Reply preview */}
              {replyTo && (
                <div className="px-3 py-2 bg-[#F5F5F7] border-t border-gray-100 flex items-center gap-2" data-testid="reply-preview">
                  <Reply className="w-4 h-4 text-[#E86A1F] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-[#E86A1F]">{replyTo.sender_name}</p>
                    <p className="text-xs text-[#6E6E73] truncate">{replyTo.content}</p>
                  </div>
                  <button onClick={() => setReplyTo(null)} className="p-1 hover:bg-gray-200 rounded"><X className="w-3 h-3 text-[#86868B]" /></button>
                </div>
              )}

              {/* Input bar */}
              <form onSubmit={sendMessage} className="p-2 border-t border-gray-100 relative">
                {/* Mention autocomplete */}
                {mentionSearch !== null && activeConv.participants && (
                  <MentionAutocomplete participants={activeConv.participants.filter(p => p.user_id !== userId)} search={mentionSearch} onSelect={handleMentionSelect} />
                )}
                <div className="flex items-center gap-2">
                  <input type="file" ref={fileInputRef} className="hidden" multiple accept="image/*,.pdf,.docx,.xlsx,.pptx,.doc,.xls,.ppt" onChange={handleFileSelect} />
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    className="p-2 rounded-lg text-[#86868B] hover:bg-[#F5F5F7] hover:text-[#E86A1F] transition disabled:opacity-40" title="Adjuntar" data-testid="widget-attach-btn">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                  </button>
                  <textarea ref={inputRef} data-testid="widget-message-input" value={newMessage}
                    onChange={e => { handleInputChange(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 80) + 'px'; }}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(e); } }}
                    placeholder="Escribe un mensaje... (@para mencionar)"
                    rows={1}
                    className="flex-1 px-3 py-2 rounded-xl bg-[#F5F5F7] border-0 text-sm focus:outline-none focus:ring-1 focus:ring-[#E86A1F]/30 text-[#1D1D1F] placeholder-[#AEAEB2] resize-none overflow-hidden"
                    style={{ maxHeight: '80px' }} />
                  <button type="submit" disabled={!newMessage.trim() || sending} data-testid="widget-send-btn"
                    className="p-2 rounded-xl bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] text-white disabled:opacity-40 transition">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}

      {/* ═══ IMAGE LIGHTBOX ═══ */}
      {lightboxImg && <ImageLightbox src={lightboxImg.src} alt={lightboxImg.alt || 'Imagen'} onClose={() => setLightboxImg(null)} />}
      {isOpen && isExpanded && <div className="fixed inset-0 bg-black/30 z-40 hidden sm:block" onClick={() => setIsExpanded(false)} />}
    </>
  );
}

function ConvListItem({ conv, userId, onClick, getConvName, isChannel: isChannelType }) {
  const lastMsg = conv.last_message;
  const lastMsgPreview = lastMsg ? (lastMsg.text === '' ? 'Archivo adjunto' : lastMsg.text) : null;
  return (
    <button data-testid={`widget-conv-${conv.conversation_id}`} onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F5F5F7] transition-colors border-b border-gray-50">
      <MiniAvatar name={getConvName(conv)} size={36} isChannel={isChannelType} />
      <div className="flex-1 text-left min-w-0">
        <p className={`text-sm truncate ${conv.unread_count > 0 ? 'font-bold text-[#1D1D1F]' : 'font-medium text-[#6E6E73]'}`}>
          {isChannelType ? '#' : ''}{getConvName(conv)}
        </p>
        {lastMsgPreview && (
          <p className="text-xs text-[#AEAEB2] truncate mt-0.5">
            {lastMsg?.sender_name && <span className="text-[#86868B]">{lastMsg.sender_name}: </span>}{lastMsgPreview}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        {lastMsg?.timestamp && <span className="text-[10px] text-[#AEAEB2]">{new Date(lastMsg.timestamp).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</span>}
        {conv.unread_count > 0 && <div className="w-5 h-5 rounded-full bg-[#e03c3c] flex items-center justify-center"><span className="text-[10px] text-white font-bold">{conv.unread_count}</span></div>}
      </div>
    </button>
  );
}
